/**
 * Theme + density application.
 *
 * Source of truth is the user record on the server (AppUser.theme,
 * AppUser.density). Once /me loads we mirror those into `data-theme` and
 * `data-density` attributes on the <html> element so the CSS variable
 * overrides in index.css take effect.
 *
 * For the pre-/me-fetch window we also persist the last-applied values
 * to localStorage so the app boots in the user's preferred theme on
 * subsequent loads (no flash of light theme during /me roundtrip).
 */

export type Theme = 'light' | 'dark';
export type Density = 'comfortable' | 'compact';

const THEME_KEY = 'tinsmith:theme';
const DENSITY_KEY = 'tinsmith:density';

export function applyTheme(theme: Theme | null): void {
  const el = document.documentElement;
  if (theme === 'dark') {
    el.setAttribute('data-theme', 'dark');
    try { window.localStorage.setItem(THEME_KEY, 'dark'); } catch { /* ignore */ }
  } else {
    el.removeAttribute('data-theme');
    if (theme === 'light') {
      try { window.localStorage.setItem(THEME_KEY, 'light'); } catch { /* ignore */ }
    } else {
      try { window.localStorage.removeItem(THEME_KEY); } catch { /* ignore */ }
    }
  }
}

export function applyDensity(density: Density | null): void {
  const el = document.documentElement;
  if (density === 'compact') {
    el.setAttribute('data-density', 'compact');
    try { window.localStorage.setItem(DENSITY_KEY, 'compact'); } catch { /* ignore */ }
  } else {
    el.removeAttribute('data-density');
    if (density === 'comfortable') {
      try { window.localStorage.setItem(DENSITY_KEY, 'comfortable'); } catch { /* ignore */ }
    } else {
      try { window.localStorage.removeItem(DENSITY_KEY); } catch { /* ignore */ }
    }
  }
}

/**
 * Apply whatever was last persisted before the /me query resolves.
 * Called once from main.tsx so the user doesn't see the light theme
 * flash before their dark setting kicks in.
 */
export function bootstrapThemeFromStorage(): void {
  try {
    const t = window.localStorage.getItem(THEME_KEY);
    if (t === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
    const d = window.localStorage.getItem(DENSITY_KEY);
    if (d === 'compact') document.documentElement.setAttribute('data-density', 'compact');
  } catch {
    /* private mode — fall back to light/comfortable defaults */
  }
}
